import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ComponentProps, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Payload as TooltipPayload } from 'recharts/types/component/DefaultTooltipContent'

import {
    IntervalDataEntry,
    IntervalReportSettings,
} from '@/hooks/useApi/useActiveManagementApi/types'
import { ActiveManagementFiltersValues } from '@/utils/constants'
import { MultiChart } from '@organisms'
import { useActiveManagementMetersQuery } from '../../hooks/useActiveManagementMetersQuery'
import { useActiveManagementSubmetersQuery } from '../../hooks/useActiveManagementSubmetersQuery'
import {
    useDataCompleteness,
    type DataCompleteness,
} from '../../hooks/useDataCompleteness'
import { detectDataGrouping } from './detectDataGrouping'
import { formatTooltipDate } from './formatTooltipDate'
import { IntervalTooltipWrapper } from './IntervalTooltipWrapper'
import { TooltipItem } from './TooltipItem'

dayjs.extend(utc)

type UseIntervalTooltipParams = {
    data: IntervalDataEntry[]
    reportSettings: IntervalReportSettings
    activeFilters: ActiveManagementFiltersValues
    meterMap: Map<number, string>
    unit: string
    highlights?: { highlightKeySet: Set<string> }
}

type UseIntervalTooltipReturn = {
    tooltipTitleFormatter: (label: string | number) => string
    tooltipWrapper: React.ComponentType<{
        label: string
        originalDateLabel?: string
        tooltipItemsWrapperClassname?: string
        children: React.ReactNode
    }>
    renderTooltipItem: ComponentProps<typeof MultiChart>['renderTooltipItem']
}

export const useIntervalTooltip = ({
    data,
    reportSettings,
    activeFilters,
    meterMap,
    unit,
    highlights,
}: UseIntervalTooltipParams): UseIntervalTooltipReturn => {
    const { t } = useTranslation('translations')
    const { data: meters } = useActiveManagementMetersQuery({ activeFilters })
    const { data: subMeters } = useActiveManagementSubmetersQuery({
        activeFilters,
    })

    // Determine which filter to use based on display setting
    const selectedMeters = useMemo(() => {
        return reportSettings.display === 'subMeters'
            ? (activeFilters.subMeter ?? [])
            : activeFilters.meter
    }, [reportSettings.display, activeFilters.meter, activeFilters.subMeter])

    // Calculate total meters based on filter state and display setting:
    // - If specific meters/subMeters are selected, use the count of selected items
    // - If none are selected (all items), use the total count of available meters or subMeters based on display
    // Note: For data completeness calculation, we use the full meter/subMeter count, not the display limit
    const metersDisplay =
        reportSettings.display === 'subMeters' ? subMeters : meters
    const totalMeters =
        selectedMeters.length > 0
            ? selectedMeters.length
            : metersDisplay?.length || 0

    const getValue = useCallback(
        (entry: IntervalDataEntry): number | undefined => {
            switch (reportSettings.dataType) {
                case 'usage':
                    return entry.usage
                case 'demandAverage':
                    return entry.demandAverage
                case 'demandMax':
                    return entry.demandMax
                default:
                    return entry.usage
            }
        },
        [reportSettings.dataType]
    )

    // Detect data grouping from data structure (not from settings)
    const dataGrouping = useMemo(
        () => detectDataGrouping(data, reportSettings.display),
        [data, reportSettings.display]
    )

    // Use the data completeness hook to calculate completeness information
    const dataCompletenessMap = useDataCompleteness({
        data,
        totalMeters,
        dataGrouping,
        getValue,
        display: reportSettings.display,
    })

    // Simple lookup function for data completeness (O(1) lookup)
    const calculateDataCompleteness = useCallback(
        (dateLabel: string): DataCompleteness => {
            return (
                dataCompletenessMap.get(dateLabel) || {
                    metersWithData: 0,
                    totalMeters,
                }
            )
        },
        [dataCompletenessMap, totalMeters]
    )

    // Pre-compute a map of timestamps to entries for O(1) lookup
    // This eliminates O(n) filtering on every tooltip hover
    const {
        map: entriesByTimestampMap,
        timestampNumberMap: entriesByTimestampNumberMap,
    } = useMemo(() => {
        const map = new Map<string, IntervalDataEntry[]>()
        const timestampNumberMap = new Map<number, IntervalDataEntry[]>()
        for (const entry of data) {
            if (entry.timestampLocal) {
                const timestamp = entry.timestampLocal
                const timestampNumber = dayjs(timestamp).valueOf()
                if (!map.has(timestamp)) {
                    map.set(timestamp, [])
                }
                if (!timestampNumberMap.has(timestampNumber)) {
                    timestampNumberMap.set(timestampNumber, [])
                }
                map.get(timestamp)?.push(entry)
                timestampNumberMap.get(timestampNumber)?.push(entry)
            }
        }
        return { map, timestampNumberMap }
    }, [data])

    const tooltipTitleFormatter = useCallback(
        (label: string | number): string => {
            // Handle numeric timestamp (from time-scale xKey)
            // This can be a number or a numeric string like "1704067200000"
            const numericValue =
                typeof label === 'number' ? label : Number(label)
            if (!isNaN(numericValue) && numericValue > 946684800000) {
                const timestampValues =
                    entriesByTimestampNumberMap.get(numericValue)

                if (timestampValues && timestampValues.length > 0) {
                    const firstEntry = timestampValues[0]
                    return formatTooltipDate(firstEntry.date)
                }

                // Valid timestamp (after year 2000)
                const date = dayjs(numericValue)
                if (date.isValid()) {
                    return date.format('MMMM D, YYYY, HH:mm')
                }
            }

            // O(1) lookup instead of O(n) filter
            // label can be timestampLocal string from chart data or a date string
            const labelStr = String(label)
            const matchingEntries = entriesByTimestampMap.get(labelStr) || []

            // If we found entries, format the timestamp
            if (matchingEntries.length > 0) {
                const firstEntry = matchingEntries[0]

                // Format as "June 27, 2025, 12:00"
                return formatTooltipDate(firstEntry.date)
            }

            // Try parsing as a date if it looks like an ISO string
            const parsedDate = dayjs(labelStr)
            if (parsedDate.isValid() && labelStr.includes('-')) {
                return parsedDate.format('MMMM D, YYYY, HH:mm')
            }

            // If no matching entries found, return label as-is
            return labelStr
        },
        [entriesByTimestampMap, entriesByTimestampNumberMap]
    )

    const tooltipWrapper = useMemo(() => {
        const TooltipWrapperComponent: React.FC<{
            label: string
            originalDateLabel?: string
            tooltipItemsWrapperClassname?: string
            children: React.ReactNode
        }> = (props) => {
            // Use originalDateLabel if available, otherwise fall back to label
            // originalDateLabel is the Unix timestamp string from chart data (e.g., '1704067200000')
            // label is the formatted date string (e.g., 'September 17, 2024, 03:00')
            // For data completeness lookup, use the Unix timestamp (originalDateLabel)
            const timestampForCompleteness =
                props.originalDateLabel || props.label

            const dataCompleteness = calculateDataCompleteness(
                timestampForCompleteness
            )

            return (
                <IntervalTooltipWrapper
                    label={props.label}
                    tooltipItemsWrapperClassname={
                        props.tooltipItemsWrapperClassname
                    }
                    dataCompleteness={dataCompleteness}
                    selectedMeters={selectedMeters}
                    meters={metersDisplay}
                    dataGrouping={dataGrouping}
                    display={reportSettings.display}
                >
                    {props.children}
                </IntervalTooltipWrapper>
            )
        }
        return TooltipWrapperComponent
    }, [
        calculateDataCompleteness,
        selectedMeters,
        metersDisplay,
        dataGrouping,
        reportSettings.display,
    ])

    const renderTooltipItem = useCallback(
        (entry: TooltipPayload<number | string, string | number>) => {
            const areAllMetersSelected =
                selectedMeters.length === 0 && metersDisplay
            let label: string

            if (dataGrouping === 'aggregate') {
                label = areAllMetersSelected
                    ? t('active_management.interval.chart_tooltip.all_meters', {
                          unit,
                      })
                    : t(
                          'active_management.interval.chart_tooltip.selected_meters',
                          {
                              count: selectedMeters.length,
                              unit,
                          }
                      )
            } else {
                // In compare mode, entry.dataKey is the meterId (as string)
                const meterIdStr = entry.dataKey as string
                const meterId = parseInt(meterIdStr, 10)

                if (isNaN(meterId)) {
                    const fallbackName = entry.name || entry.dataKey
                    label = `${String(fallbackName)} (${unit})`
                } else {
                    const meterName = meterMap.get(meterId)
                    label = `${meterName || String(meterId)} (${unit})`
                }
            }

            const payload = entry.payload as Record<string, unknown> | undefined
            const timestamp =
                payload?.timestamp !== undefined
                    ? String(payload.timestamp)
                    : undefined
            const dataKey = entry.dataKey as string | undefined
            const pointKey =
                timestamp !== undefined
                    ? dataGrouping === 'aggregate'
                        ? timestamp
                        : `${timestamp}|${dataKey ?? ''}`
                    : ''
            const showHigh = Boolean(
                highlights && highlights.highlightKeySet.has(pointKey)
            )

            return (
                <TooltipItem
                    label={label}
                    value={entry.value}
                    color={entry.color || ''}
                    showHigh={showHigh}
                />
            )
        },
        [
            dataGrouping,
            selectedMeters,
            metersDisplay,
            unit,
            meterMap,
            t,
            highlights,
        ]
    ) as ComponentProps<typeof MultiChart>['renderTooltipItem']

    return {
        tooltipTitleFormatter,
        tooltipWrapper,
        renderTooltipItem,
    }
}
